import { useQuery } from '@tanstack/react-query';
import { aufgabenApi } from '../api/aufgaben';
import type { CityMember } from '../types';

interface AssignmentPickerProps {
  value: number | null;
  onChange: (userId: number | null, member: CityMember | null) => void;
  allowUnassign?: boolean;
  disabled?: boolean;
  id?: string;
}

/**
 * Dropdown listing active members of the caller's city. Pre-filled from
 * GET /aufgaben/members. Always includes a "— niemand —" option when
 * `allowUnassign` is true (default).
 */
export default function AssignmentPicker({
  value,
  onChange,
  allowUnassign = true,
  disabled,
  id,
}: AssignmentPickerProps) {
  const { data: members = [], isLoading } = useQuery({
    queryKey: ['aufgaben', 'members'],
    queryFn: aufgabenApi.members,
    staleTime: 60_000,
  });

  return (
    <select
      id={id}
      value={value ?? ''}
      disabled={disabled || isLoading}
      onChange={(e) => {
        const raw = e.target.value;
        if (raw === '') {
          onChange(null, null);
          return;
        }
        const uid = Number(raw);
        const m = members.find((x) => x.id === uid) ?? null;
        onChange(uid, m);
      }}
      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-50"
    >
      {allowUnassign && <option value="">— niemand —</option>}
      {members.map((m) => (
        <option key={m.id} value={m.id}>
          {m.full_name} {m.role === 'city_admin' ? '(Admin)' : ''}
        </option>
      ))}
    </select>
  );
}
